import React, { useState, useEffect } from 'react';
import {
   Map, Tent, Calendar, Clock, MapPin, Search,
   ChevronDown, Heart, Star, Users, CheckCircle2,
   ArrowRight, Filter, PlayCircle, Map as MapIcon
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const customPin = typeof window !== 'undefined' && L && L.divIcon ? L.divIcon({
   className: 'custom-map-pin',
   html: '<div class="w-4 h-4 bg-red-600 rounded-full border-2 border-white shadow-[0_0_10px_rgba(220,38,38,0.8)]"></div>',
   iconSize: [16, 16],
   iconAnchor: [8, 8]
}) : null;

const containerVariants = {
   hidden: { opacity: 0 },
   show: {
      opacity: 1,
      transition: {
         staggerChildren: 0.08
      }
   }
};

export default function Camps() {
   const navigate = useNavigate();
   const [campsData, setCampsData] = useState({ running: [], upcoming: [], past: [] });
   const [categories, setCategories] = useState([]);
   const [shows, setShows] = useState([]);
   const [activeTab, setActiveTab] = useState('running');
   const [loading, setLoading] = useState(true);
   const [searchQuery, setSearchQuery] = useState('');
   const [districtFilter, setDistrictFilter] = useState('All');
   const [partyFilter, setPartyFilter] = useState('All');

   const fetchData = async () => {
      try {
         setLoading(true);
         const headers = {
            'Client-ID': 'lokanatya_client',
            'Client-Secret': 'super_secret_key_123'
         };

         const [campsRes, homeRes, showsRes] = await Promise.all([
            axios.get(`/api/fetch_camps`, { headers }).catch(() => ({ data: null })),
            axios.get(`/api/fetch_homepage_data`, { headers }).catch(() => ({ data: null })),
            axios.get(`/api/fetch_product_list`, { headers }).catch(() => ({ data: null }))
         ]);

         if (campsRes.data) {
            setCampsData(campsRes.data);
         }
         if (homeRes.data && homeRes.data.category) {
            setCategories(Array.isArray(homeRes.data.category) ? homeRes.data.category : Object.values(homeRes.data.category));
         }
         if (showsRes.data && showsRes.data.data) {
            const safeData = Array.isArray(showsRes.data.data) ? showsRes.data.data : Object.values(showsRes.data.data);
            setShows(safeData);
         }
      } catch (error) {
         console.error('Error fetching data:', error);
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      window.scrollTo(0, 0);
      fetchData();
   }, []);

   const runningArr = Array.isArray(campsData?.running) ? campsData.running : (typeof campsData?.running === 'object' && campsData?.running ? Object.values(campsData.running) : []);
   const upcomingArr = Array.isArray(campsData?.upcoming) ? campsData.upcoming : (typeof campsData?.upcoming === 'object' && campsData?.upcoming ? Object.values(campsData.upcoming) : []);
   const pastArr = Array.isArray(campsData?.past) ? campsData.past : (typeof campsData?.past === 'object' && campsData?.past ? Object.values(campsData.past) : []);
   const categoriesArr = Array.isArray(categories) ? categories : (typeof categories === 'object' && categories ? Object.values(categories) : []);

   const tabData = campsData?.[activeTab];
   const currentCamps = Array.isArray(tabData) ? tabData : (typeof tabData === 'object' && tabData ? Object.values(tabData) : []);

   const allCampsList = [...runningArr, ...upcomingArr, ...pastArr];
   const availableDistricts = [...new Set(allCampsList.map(c => c.district_name || c.camp_location).filter(Boolean))].sort();
   const availableParties = [...new Set(categoriesArr.map(c => c.cat_name).filter(Boolean))].sort();

   const filteredCamps = currentCamps.filter(camp => {
      const jatra = categoriesArr.find(c => c.id === camp.cat_id);
      const name = camp.cat_name || jatra?.cat_name || 'Camp';
      const loc = camp.camp_location || camp.district_name || 'Odisha';

      const matchesSearch = name.toLowerCase().includes(searchQuery.toLowerCase()) || loc.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesDistrict = districtFilter === 'All' || loc === districtFilter;
      const matchesParty = partyFilter === 'All' || name === partyFilter;

      return matchesSearch && matchesDistrict && matchesParty;
   });

   const handleCampClick = (camp) => {
      const matchingShow = shows.find(s => Number(s.cat_id) === Number(camp.cat_id) && s.status === 1);
      if (matchingShow) {
         navigate(`/shows/${matchingShow.product_id || matchingShow.jatra_id}`);
      } else {
         Swal.fire({
            title: 'No Shows Available',
            text: 'There are no active shows available for this camp currently.',
            icon: 'info',
            confirmButtonColor: '#0f172a',
            confirmButtonText: 'Browse Other Shows',
            customClass: {
               popup: 'rounded-2xl',
               confirmButton: 'rounded-xl px-6 py-2.5 font-semibold tracking-wide'
            }
         }).then((result) => {
            if (result.isConfirmed) {
               navigate('/shows');
            }
         });
      }
   };

   const formatCampDate = (dateString) => {
      if (!dateString) return 'NA';
      const d = new Date(dateString);
      return isNaN(d) ? dateString : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
   };

   return (
      <div className="bg-slate-50 min-h-screen pb-24 text-slate-800 selection:bg-red-500/20 selection:text-red-900">

         {/* Hero Banner with Jatra Tent Background */}
         <div className="relative w-full h-[360px] sm:h-[400px] md:h-[440px] bg-[#0b1a4a] overflow-hidden flex items-center shadow-lg border-b border-blue-900/30">
            {/* Background Image */}
            <div className="absolute inset-0 z-0 flex justify-end">
               <img
                  src="images/camps_banner_bg.jpg"
                  alt="Jatra Party Camps"
                  className="w-full h-full object-cover object-right-top md:object-right filter contrast-105 brightness-100"
               />
               {/* Smooth gradient transition from solid dark blue left to transparent right */}
               <div className="absolute inset-0 bg-gradient-to-r from-[#0b1a4a] via-[#0b1a4a]/90 to-transparent w-full md:w-1/2 z-10 pointer-events-none"></div>
               <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[#0b1a4a] to-transparent z-10 pointer-events-none"></div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-20 flex items-center justify-between">
               <div className="flex-1 text-white max-w-lg py-6">
                  <span className="inline-flex items-center gap-1.5 bg-blue-500/20 border border-blue-400/30 text-blue-300 font-extrabold text-[10px] sm:text-xs tracking-widest uppercase px-3.5 py-1 rounded-full mb-3 backdrop-blur-md">
                     🎪 PARTY CAMPS ACROSS ODISHA
                  </span>
                  <h1 className="text-3xl sm:text-5xl md:text-6xl font-black text-white tracking-tight mb-3 drop-shadow-md">
                     Party Camps
                  </h1>
                  <p className="text-blue-100 text-xs sm:text-sm md:text-base font-medium max-w-md mb-6 leading-relaxed drop-shadow-sm">
                     Discover where your favourite Jatra parties are currently camped, explore their upcoming locations, and get directions.
                  </p>

                  {/* Sleek Glassmorphism Search Bar */}
                  <div className="relative max-w-md">
                     <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search by camp, party or location..."
                        className="w-full bg-white/15 hover:bg-white/20 focus:bg-white/25 backdrop-blur-md border border-white/30 text-white rounded-full py-3 sm:py-3.5 pl-10 pr-10 text-xs sm:text-sm font-medium outline-none focus:ring-2 focus:ring-red-500/60 transition-all placeholder:text-blue-200 shadow-xl"
                     />
                     <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-200">
                        <Search size={16} />
                     </div>
                     {searchQuery && (
                        <button
                           onClick={() => setSearchQuery('')}
                           className="absolute right-3.5 top-1/2 -translate-y-1/2 text-blue-200 hover:text-white text-xs font-bold bg-white/20 hover:bg-white/30 w-5 h-5 rounded-full flex items-center justify-center transition-colors"
                        >
                           ✕
                        </button>
                     )}
                  </div>
               </div>
            </div>
         </div>

         {/* Main Content Container */}
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 md:mt-16">

            {/* Explore Camps Section Header & Tabs */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 border-b border-slate-200/70 pb-6">

               <div>
                  <div className="flex items-center gap-3 mb-2">
                     <div className="w-1.5 h-6 bg-red-500 rounded-full"></div>
                     <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">Explore Camps</h2>
                  </div>
                  <p className="text-slate-500 text-sm font-medium ml-4">Browse all active and scheduled jatra camps</p>
               </div>

               <div className="flex items-center gap-2 overflow-x-auto hide-scroll-bar bg-white p-1.5 rounded-2xl shadow-sm border border-slate-100">
                  <button
                     onClick={() => setActiveTab('running')}
                     className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${activeTab === 'running' ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                  >
                     <CheckCircle2 size={16} strokeWidth={2.5} className={activeTab === 'running' ? 'text-emerald-100' : 'text-emerald-500'} /> Running
                  </button>
                  <button
                     onClick={() => setActiveTab('upcoming')}
                     className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${activeTab === 'upcoming' ? 'bg-amber-500 text-white shadow-md shadow-amber-500/20' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                  >
                     <Calendar size={16} strokeWidth={2.5} className={activeTab === 'upcoming' ? 'text-amber-100' : 'text-amber-500'} /> Upcoming
                  </button>
                  <button
                     onClick={() => setActiveTab('past')}
                     className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${activeTab === 'past' ? 'bg-slate-800 text-white shadow-md shadow-slate-800/20' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                  >
                     <Tent size={16} strokeWidth={2.5} className={activeTab === 'past' ? 'text-slate-300' : 'text-slate-400'} /> Past
                  </button>
               </div>
            </div>

            {/* Advanced Filters Row */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
               <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2.5 sm:gap-3 w-full lg:w-auto">
                  <div className="relative w-full sm:w-auto">
                     <select
                        value={districtFilter}
                        onChange={(e) => setDistrictFilter(e.target.value)}
                        className="w-full appearance-none bg-white border border-slate-200/80 rounded-xl pl-4 pr-9 py-2.5 text-[12px] sm:text-[13px] font-medium text-slate-600 cursor-pointer hover:border-slate-300 hover:bg-slate-50 transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                     >
                        <option value="All">All Districts</option>
                        {availableDistricts.map(d => <option key={d} value={d}>{d}</option>)}
                     </select>
                     <ChevronDown size={14} strokeWidth={2} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>

                  <div className="relative w-full sm:w-auto">
                     <select
                        value={partyFilter}
                        onChange={(e) => setPartyFilter(e.target.value)}
                        className="w-full appearance-none bg-white border border-slate-200/80 rounded-xl pl-4 pr-9 py-2.5 text-[12px] sm:text-[13px] font-medium text-slate-600 cursor-pointer hover:border-slate-300 hover:bg-slate-50 transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                     >
                        <option value="All">All Parties</option>
                        {availableParties.map(p => <option key={p} value={p}>{p}</option>)}
                     </select>
                     <ChevronDown size={14} strokeWidth={2} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>

                  <div className="relative w-full sm:w-auto">
                     <select className="w-full appearance-none bg-white border border-slate-200/80 rounded-xl pl-4 pr-9 py-2.5 text-[12px] sm:text-[13px] font-medium text-slate-600 cursor-pointer hover:border-slate-300 hover:bg-slate-50 transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                        <option>Any Date</option>
                        <option>This Week</option>
                        <option>This Month</option>
                     </select>
                     <ChevronDown size={14} strokeWidth={2} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>

                  <div className="relative w-full sm:w-auto">
                     <select className="w-full appearance-none bg-white border border-slate-200/80 rounded-xl pl-4 pr-9 py-2.5 text-[12px] sm:text-[13px] font-medium text-slate-600 cursor-pointer hover:border-slate-300 hover:bg-slate-50 transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                        <option>Language</option>
                        <option>Odia</option>
                        <option>Bengali</option>
                     </select>
                     <ChevronDown size={14} strokeWidth={2} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>

                  <div className="flex items-center justify-center gap-2 bg-white px-4 py-2.5 rounded-xl border border-slate-200/80 shadow-sm hover:border-slate-300 transition-all cursor-pointer w-full sm:w-auto">
                     <input type="checkbox" id="runningOnly" className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer" defaultChecked={activeTab === 'running'} />
                     <label htmlFor="runningOnly" className="text-[12px] sm:text-[13px] font-medium text-slate-600 cursor-pointer select-none pt-px">Live Only</label>
                  </div>
               </div>

               <div className="flex items-center justify-between bg-white border border-slate-200/80 rounded-xl px-4 py-2.5 text-[12px] sm:text-[13px] font-medium text-slate-700 cursor-pointer hover:border-slate-300 shadow-sm transition-all w-full sm:w-auto">
                  <span className="text-slate-500 mr-2">Sort by:</span> Popular <ChevronDown size={14} strokeWidth={2} className="ml-3 text-slate-400" />
               </div>
            </div>

            {/* Camp Grid */}
            {loading ? (
               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <div key={i} className="h-[420px] bg-white rounded-3xl animate-pulse shadow-sm border border-slate-100"></div>)}
               </div>
            ) : filteredCamps.length === 0 ? (
               <motion.div
                  initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-32"
               >
                  <div className="w-24 h-24 bg-slate-200/50 rounded-full flex items-center justify-center mx-auto mb-6">
                     <Search size={40} className="text-slate-400" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-800 mb-3">No camps found</h3>
                  <p className="text-slate-500 max-w-sm mx-auto text-base">We couldn't find any {activeTab} camps matching your criteria. Try adjusting your filters or search term.</p>
               </motion.div>
            ) : (
               <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="show"
                  className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-8"
               >
                  <AnimatePresence mode="popLayout">
                     {filteredCamps.map((camp, index) => {
                        const jatra = Array.isArray(categories) ? categories.find(c => c.id === camp.cat_id) : null;
                        const name = camp.cat_name || jatra?.cat_name || 'Camp';
                        const image = camp.camp_image || jatra?.cat_image || 'https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg';
                        const logo = jatra?.cat_image || 'https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg';
                        const location = camp.camp_location || camp.district_name || 'Odisha';
                        const dateRange = `${formatCampDate(camp.camp_from)} - ${formatCampDate(camp.camp_to)}`;

                        let badgeColor = "bg-emerald-500 shadow-emerald-500/30";
                        let badgeText = "LIVE NOW";
                        if (activeTab === 'upcoming') { badgeColor = "bg-amber-500 shadow-amber-500/30"; badgeText = "UPCOMING"; }
                        if (activeTab === 'past') { badgeColor = "bg-slate-600 shadow-slate-600/30"; badgeText = "PAST"; }

                        return (
                           <motion.div
                              key={camp.id || index}
                              layout
                              initial={{ opacity: 0, y: 30 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.95 }}
                              transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
                              onClick={() => handleCampClick(camp)}
                              className="group cursor-pointer bg-white rounded-2xl sm:rounded-3xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100/80 overflow-hidden flex flex-col justify-between h-full relative"
                           >
                              {/* Top Image Container */}
                              <div className="relative aspect-[4/3] sm:h-[200px] w-full overflow-hidden bg-gray-100 shrink-0">
                                 <img
                                    src={image}
                                    alt={name}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    onError={(e) => { e.target.src = "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg" }}
                                 />
                                 
                                 {/* Top Left Status Badge */}
                                 <div className="absolute top-2 left-2 sm:top-3 sm:left-3 z-10 bg-red-600 text-white text-[9px] sm:text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md shadow-md max-w-[85px] sm:max-w-[130px] truncate">
                                    {activeTab === 'running' ? 'LIVE NOW' : (activeTab === 'upcoming' ? 'UPCOMING' : 'PAST')}
                                 </div>

                                 {/* Top Right Party Logo */}
                                 <div className="absolute top-2 right-2 sm:top-3 sm:right-3 z-10 w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-white/80 backdrop-blur-md border border-white/40 shadow-sm flex items-center justify-center overflow-hidden shrink-0">
                                    <img src={logo} alt="logo" className="w-full h-full object-cover" onError={(e) => e.target.src = "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg"} />
                                 </div>
                              </div>

                              {/* Card Content Body */}
                              <div className="p-2.5 sm:p-4 flex flex-col justify-between flex-1 bg-white">
                                 <div>
                                    {/* Camp Name (100% visible, crisp text) */}
                                    <h3 className="text-xs sm:text-base font-bold text-gray-900 leading-snug line-clamp-2 min-h-[2rem] sm:min-h-[2.5rem] mb-1.5 sm:mb-2 group-hover:text-red-600 transition-colors" title={name}>
                                       {name}
                                    </h3>

                                    {/* Date & Location Rows */}
                                    <div className="space-y-1 text-[10px] sm:text-xs font-medium text-gray-500">
                                       <div className="flex items-center text-gray-600 truncate">
                                          <Calendar size={12} className="mr-1.5 text-red-500 shrink-0 sm:w-3.5 sm:h-3.5" />
                                          <span className="truncate">{dateRange}</span>
                                       </div>
                                       <div className="flex items-center text-gray-500 truncate">
                                          <MapPin size={12} className="mr-1.5 text-red-400 shrink-0 sm:w-3.5 sm:h-3.5" />
                                          <span className="truncate">{location}</span>
                                       </div>
                                    </div>
                                 </div>
                              </div>

                              {/* Full Width Bottom Button */}
                              <div className="bg-red-600 hover:bg-red-700 group-hover:bg-red-700 text-white font-bold py-2 sm:py-3 px-3 text-xs sm:text-sm flex items-center justify-center gap-1.5 sm:gap-2 transition-colors mt-auto shrink-0">
                                 View Shows <ArrowRight size={14} strokeWidth={2.5} className="sm:w-4 sm:h-4" />
                              </div>
                           </motion.div>
                        )
                     })}
                  </AnimatePresence>
               </motion.div>
            )}

            {/* Bottom Features Section */}
            {!loading && (
               <div className="mt-20 grid grid-cols-1 lg:grid-cols-5 gap-8">

                  {/* Map Section */}
                  <div className="lg:col-span-3 bg-[#0f172a] rounded-[1.5rem] p-6 md:p-8 flex flex-col relative overflow-hidden shadow-xl shadow-slate-900/10">
                     {/* Header */}
                     <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6 z-20 relative">
                        <div>
                           <div className="flex items-center gap-3 mb-2">
                              <MapPin size={28} className="text-red-500" strokeWidth={2.5} />
                              <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight">Find Camps on Map</h3>
                           </div>
                           <p className="text-sm md:text-base text-slate-300 font-normal leading-relaxed max-w-sm">
                              Explore all running and upcoming jatra camps across Odisha.<br />Click on any location to view camp details and shows.
                           </p>
                        </div>

                     </div>

                     {/* Map */}
                     <div className="w-full h-[320px] rounded-2xl overflow-hidden border border-slate-700/50 shadow-inner relative z-10 bg-slate-800 mb-6">
                        <MapContainer
                           center={[20.2376, 84.2700]}
                           zoom={7}
                           minZoom={7}
                           maxBounds={[[17.5, 81.0], [23.0, 88.0]]}
                           maxBoundsViscosity={1.0}
                           scrollWheelZoom={false}
                           className="w-full h-full z-0"
                        >
                           <TileLayer
                              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                           />
                           {allCampsList.filter(camp => camp.latitude && camp.longitude).map((camp, idx) => {
                              const jatra = categoriesArr.find(c => c.id === camp.cat_id);
                              const name = camp.cat_name || jatra?.cat_name || 'Camp';
                              return (
                                 <Marker key={idx} position={[parseFloat(camp.latitude), parseFloat(camp.longitude)]} icon={customPin}>
                                    <Popup>
                                       <div className="text-center min-w-[150px]">
                                          <strong className="text-sm font-bold text-slate-800">{name}</strong><br />
                                          <span className="text-slate-600 text-xs leading-tight">{camp.camp_location || camp.district_name}</span><br />
                                          <span className="text-slate-500 text-[10px] mt-1 mb-2 block font-medium">{formatCampDate(camp.camp_from)} - {formatCampDate(camp.camp_to)}</span>
                                          <a
                                             href={`https://www.google.com/maps/dir/?api=1&destination=${camp.latitude},${camp.longitude}`}
                                             target="_blank"
                                             rel="noopener noreferrer"
                                             className="mt-1 flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold py-1.5 px-3 rounded-lg transition-colors w-full !text-white no-underline"
                                          >
                                             <MapPin size={12} /> Get Directions
                                          </a>
                                       </div>
                                    </Popup>
                                 </Marker>
                              )
                           })}
                        </MapContainer>
                     </div>

                     {/* Stats */}
                     <div className="grid grid-cols-3 divide-x divide-slate-700/50 pt-2 z-20 relative">
                        <div className="flex items-center gap-3 justify-center">
                           <MapPin size={20} className="text-red-500" strokeWidth={2.5} />
                           <div>
                              <div className="text-white font-bold text-lg md:text-xl leading-none mb-1">{allCampsList.length}+</div>
                              <div className="text-slate-400 font-medium text-xs">Active Camps</div>
                           </div>
                        </div>
                        <div className="flex items-center gap-3 justify-center">
                           <Calendar size={20} className="text-yellow-500" strokeWidth={2.5} />
                           <div>
                              <div className="text-white font-bold text-lg md:text-xl leading-none mb-1">120+</div>
                              <div className="text-slate-400 font-medium text-xs">Upcoming Shows</div>
                           </div>
                        </div>
                        <div className="flex items-center gap-3 justify-center">
                           <Users size={20} className="text-purple-500" strokeWidth={2.5} />
                           <div>
                              <div className="text-white font-bold text-lg md:text-xl leading-none mb-1">50K+</div>
                              <div className="text-slate-400 font-medium text-xs">Happy Visitors</div>
                           </div>
                        </div>
                     </div>
                  </div>

                  {/* Popular Camps Section */}
                  <div className="lg:col-span-2 bg-white rounded-[1.5rem] p-6 md:p-8 border border-slate-100 shadow-xl shadow-slate-200/40 flex flex-col relative overflow-hidden">
                     <div className="flex justify-between items-start mb-6">
                        <div>
                           <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-1 tracking-tight">Popular Camps</h3>
                           <p className="text-xs md:text-sm text-slate-500 font-medium">Top camps loved by our audience</p>
                        </div>
                        <button className="text-[11px] md:text-xs font-bold text-slate-600 bg-slate-50 px-3 py-1.5 md:px-4 md:py-2 rounded-lg hover:bg-slate-100 transition flex items-center gap-1 border border-slate-200">
                           View All <ChevronDown size={14} className="-rotate-90" />
                        </button>
                     </div>

                     <div className="flex flex-col gap-3 overflow-y-auto max-h-[460px] hide-scroll-bar px-1 pb-2">
                        {allCampsList.slice(0, 5).map((camp, i) => {
                           const jatra = categories.find(c => c.id === camp.cat_id);
                           const isRunning = new Date(camp.camp_to) >= new Date();
                           return (
                              <div key={i} onClick={() => handleCampClick(camp)} className="flex items-center gap-4 p-3.5 group cursor-pointer bg-white border border-slate-100 rounded-2xl hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 transition-all">
                                 <div className="w-14 h-14 md:w-16 md:h-16 shrink-0 rounded-xl border border-slate-100 shadow-sm bg-white overflow-hidden p-1.5 group-hover:border-slate-200 transition-colors">
                                    <img src={jatra?.cat_image || "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg"} alt={camp.cat_name} className="w-full h-full object-cover rounded-lg" onError={(e) => e.target.src = "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg"} />
                                 </div>
                                 <div className="flex-1 min-w-0">
                                    <h4 className="text-sm md:text-base font-bold text-slate-800 mb-0.5 truncate group-hover:text-red-600 transition-colors">{camp.cat_name || jatra?.cat_name || 'Camp'}</h4>
                                    <p className="text-xs text-slate-500 mb-1.5 truncate">{camp.camp_location || camp.district_name}, Odisha</p>
                                    <span className={`inline-block text-[9px] uppercase tracking-wider font-bold px-2.5 py-0.5 rounded-full ${isRunning ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-orange-50 text-orange-500 border border-orange-100'}`}>
                                       {isRunning ? 'RUNNING' : 'UPCOMING'}
                                    </span>
                                 </div>
                                 <div className="w-8 h-8 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0 text-slate-400 group-hover:bg-red-50 group-hover:border-red-100 group-hover:text-red-500 transition-all">
                                    <ChevronDown size={14} className="-rotate-90" />
                                 </div>
                              </div>
                           )
                        })}
                        {allCampsList.length === 0 && (
                           <div className="text-sm text-slate-400 font-medium py-4 text-center">Loading camps...</div>
                        )}
                     </div>
                  </div>
               </div>
            )}

         </div>
      </div>
   );
}
